import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getGetTasksQueryKey } from 'api/index';
import type { AxiosInstance } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import z from 'zod/v4';

export const CreateTaskVariablesSchema = z.object({
  debtorId: z.string().optional(),
  invoiceIds: z.array(z.string()),
  description: z.string().optional(),
  dueDate: z.string().optional(),
  startOffsetDays: z.number().optional(),
  taskTypeId: z.string(),
  title: z.string().min(1),
  userId: z.coerce.string().optional(),
  additionalAssigneeIds: z.array(z.string()).default([]),
  assignToAccountManager: z.boolean().optional(),
});

export type CreateTaskVariables = z.infer<typeof CreateTaskVariablesSchema>;

const CreateTaskResponseSchema = z.object({
  taskId: z.coerce.string(),
  message: z.string(),
});

type CreateTaskResponse = z.infer<typeof CreateTaskResponseSchema>;

export async function createTaskFn(
  axiosInstance: Promise<AxiosInstance>,
  data: CreateTaskVariables,
) {
  const instance = await axiosInstance;
  const response = await instance.post('tasks', CreateTaskVariablesSchema.parse(data));

  return CreateTaskResponseSchema.parse(response.data);
}

export function useCreateTask() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<CreateTaskResponse, ApiError, CreateTaskVariables>({
    mutationFn: data => createTaskFn(axiosInstance, data),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: getGetTasksQueryKey() });
      client.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  return addActionNameToMutationResult<
    CreateTaskResponse,
    ApiError,
    CreateTaskVariables,
    'createTask'
  >('createTask', mutation);
}
